export type IVariable<T extends object> = keyof T

export type ILogic = 'Or' | 'And'

export type ISortDirection = 'ASC' | 'DESC'

export interface ISeedModel<T> {
  key: string
  field: keyof T
}

export interface IFilterOption {
  logic: ILogic
  ignoreEmpty: boolean
}

export interface IRequestParamOption {
  ignoreEmpty: boolean
}

export interface ISortOption {
  direction: ISortDirection
}

export type ISortStore<T extends object> = { [key in keyof T]?: ISortDirection }

export type IAllOptions = IFilterOption & IRequestParamOption & ISortOption

export type IRequestParamResult<T extends object, U extends object = {}> = {
  sort?: ISortStore<T>[]
  skip?: number
  take?: number
  filter?: string
  noTotal?: boolean
  seedKey?: string
  seedField?: string
} & U

export class RequestParam<T extends object = any, U extends object = {}> {
  private readonly _option?: Partial<IRequestParamOption>
  constructor(option?: Partial<IRequestParamOption>) {
    this._option = option
  }

  private mergeOptions = (...p: any[]): Partial<IAllOptions> => {
    return p.reduce((a, b) => {
      return Object.assign(a, b ?? {})
    }, {})
  }

  private ignoreEmpty = (field2: string, option?: Partial<IFilterOption>) => {
    const mOption = this.mergeOptions(this._option, option)
    return !field2 && mOption?.ignoreEmpty === true
  }

  private _filter: string[] = []
  private _filterBase = (field1: IVariable<T>, operation: string, field2: string, option?: Partial<IFilterOption>) => {
    const logicStr = this._filter.length ? ' ' + (option?.logic === 'And' ? '&&' : '||') + ' ' : ''
    this._filter.push(`${logicStr}${field1 as string}${operation}${field2 as string}`)
    return this
  }
  filter = (field1: IVariable<T>, field2: string | number, option?: Partial<IFilterOption> & { noQuote?: boolean }) => {
    if (typeof field2 === 'string' && this.ignoreEmpty(field2, option)) return this
    return this._filterBase(field1, '==', typeof field2 === 'string' ? (option?.noQuote ? field2 : `"${field2}"`) : field2.toString(), option)
  }
  filterEqual = (field1: IVariable<T>, field2: string, option?: Partial<IFilterOption> & { noQuote?: boolean }) => {
    if (this.ignoreEmpty(field2, option)) return this
    return this._filterBase(field1, '==', option?.noQuote ? field2 : `"${field2}"`, option)
  }
  filterNotEquals = (field1: IVariable<T>, field2: string | number, option?: Partial<IFilterOption> & { noQuote?: boolean }) => {
    if (typeof field2 === 'string' && this.ignoreEmpty(field2, option)) return this
    const valueStr = typeof field2 === 'string' ? (option?.noQuote ? field2 : `"${field2}"`) : field2.toString()
    return this._filterBase(field1, '!=', valueStr, option)
  }
  filterEnum = (field1: IVariable<T>, field2: string | number, option?: Partial<IFilterOption>) => {
    if (typeof field2 === 'string' && this.ignoreEmpty(field2, option)) return this
    return this._filterBase(field1, '==', typeof field2 === 'string' ? field2 : field2.toString(), option)
  }
  filterStartsWith = (field1: IVariable<T>, field2: string, option?: Partial<IFilterOption>) => {
    if (this.ignoreEmpty(field2, option)) return this
    return this._filterBase(field1, '.startsWith("' + (field2 as string) + '")', '', option)
  }
  filterContains = (field1: IVariable<T>, field2: string, option?: Partial<IFilterOption>) => {
    if (this.ignoreEmpty(field2, option)) return this
    return this._filterBase(field1, '.Contains("' + (field2 as string) + '")', '', option)
  }
  filterLast = (field1: IVariable<T>, field2: string, option?: Partial<IFilterOption>) => {
    if (this.ignoreEmpty(field2, option)) return this
    return this._filterBase(field1, '.endsWith("' + (field2 as string) + '")', '', option)
  }
  filterNumber = (field1: IVariable<T>, operator: string, field2: string, option?: Partial<IFilterOption>) => {
    if (this.ignoreEmpty(field2, option)) return this
    return this._filterBase(field1, ` ${operator} ` + (field2 as string), '', option)
  }
  filterGreater = (field1: IVariable<T>, field2: string, option?: Partial<IFilterOption>) => {
    if (this.ignoreEmpty(field2, option)) return this
    return this._filterBase(field1, " > '" + (field2 as string) + "'", '', option)
  }
  filterGreaterEqual = (field1: IVariable<T>, field2: string, option?: Partial<IFilterOption>) => {
    if (this.ignoreEmpty(field2, option)) return this
    return this._filterBase(field1, " >= '" + (field2 as string) + "'", '', option)
  }
  filterLess = (field1: IVariable<T>, field2: string, option?: Partial<IFilterOption>) => {
    if (this.ignoreEmpty(field2, option)) return this
    return this._filterBase(field1, " < '" + (field2 as string) + "'", '', option)
  }
  filterLessEqual = (field1: IVariable<T>, field2: string, option?: Partial<IFilterOption>) => {
    if (this.ignoreEmpty(field2, option)) return this
    return this._filterBase(field1, " <= '" + (field2 as string) + "'", '', option)
  }
  filterCustom = (field1: string, option?: Partial<IFilterOption>) => {
    const logicStr = this._filter.length ? ' ' + (option?.logic === 'And' ? '&&' : '||') + ' ' : ''
    this._filter.push(`${logicStr}${field1 as string}`)
    return this
  }

  scope = (action: (crd: RequestParam<T>) => RequestParam<T>, option?: Partial<IFilterOption>) => {
    const crd = action(new RequestParam(this._option))
    const temp = crd.buildFilter()
    if (temp) {
      const logicStr = this._filter.length ? ' ' + (option?.logic === 'And' ? '&&' : '||') + ' ' : ''
      this._filter.push(`${logicStr}(${crd.buildFilter()})`)
    }
    this.mergeExtendData(crd.extendData as U)
    return this
  }

  private seedStore?: ISeedModel<T>
  seed = (field: keyof T, key: string) => {
    this.seedStore = { field, key }
    return this
  }

  private _skip?: number
  skip = (skip: number) => {
    this._skip = skip
    return this
  }

  private _take?: number
  take = (take: number) => {
    this._take = take
    return this
  }

  private sortStore: ISortStore<T>[] = []
  private sortBase = (field1: IVariable<T>, direction: ISortDirection, option?: Partial<ISortOption>) => {
    const temp = {} as ISortStore<T>
    temp[field1] = direction
    if (!this.sortStore.some((item) => Object.keys(item)[0] === Object.keys(temp)[0])) {
      this.sortStore.push(temp)
    }
    return this
  }
  sort = (field1: IVariable<T>, option?: Partial<ISortOption>) => {
    this.sortBase(field1, option?.direction ? option.direction : 'ASC')
    return this
  }
  private _noTotal?: boolean
  noTotal = (value: boolean = true) => {
    this._noTotal = value
    return this
  }

  private buildFilter = () => {
    return this._filter.join('')
  }

  private buildPage = () => {
    const temp = {} as any
    if (this._skip) {
      temp.skip = this._skip
    }
    if (this._take) {
      temp.take = this._take
    }
    return temp as { skip: number; take: number }
  }

  private buildRandomWithSeedKey = (): Partial<ISeedModel<T>> => {
    if (this.seedStore?.field && !this.seedStore?.key) {
      console.warn('RandomWithSeedKey missing seedKey.')
    }
    if (this.seedStore?.key && !this.seedStore?.field) {
      console.warn('RandomWithSeedKey missing seedField.')
    }
    if (!this.seedStore?.key && !this.seedStore?.field) {
      return {}
    }
    return this.seedStore
  }

  private _extendData: U = {} as U
  private get extendData(): U {
    return this._extendData
  }
  private mergeExtendData = (source: U) => {
    for (const key in source) {
      this._extendData[key] = source[key]
    }
  }
  extendResult = (customKey: keyof U, value: U[keyof U]) => {
    if (!this._extendData) {
      this._extendData = {} as U
    }
    this._extendData[customKey] = value
    return this
  }

  build = (): IRequestParamResult<T, U> => {
    const obj: IRequestParamResult<T, U> = { ...this.buildPage() } as IRequestParamResult<T, U>
    if (this._extendData) {
      for (const key in this._extendData) {
        obj[key] = this._extendData[key]
      }
    }
    if (this.sortStore.length > 0) obj.sort = this.sortStore
    const filter = this.buildFilter()
    if (filter) obj.filter = filter
    if (this._noTotal) obj.noTotal = this._noTotal
    const seedInfo = this.buildRandomWithSeedKey()
    if (!!seedInfo.key && !!seedInfo.field) {
      obj.seedField = seedInfo.field.toString()
      obj.seedKey = seedInfo.key
    }
    return obj
  }
}

export const createRequestBuilder = <T extends object, U extends object = {}>(option?: Partial<IRequestParamOption>) => {
  return new RequestParam<T, U>(option)
}
